import Vue from 'vue';

const STORAGE_KEY = 'chatHistory_v1';
const MAX_MESSAGES_PER_SESSION = 50;

function loadPersistedState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);

    if (!raw) return { sessions: {} };

    const parsed = JSON.parse(raw);

    if (!parsed || typeof parsed !== 'object') return { sessions: {} };
    if (!parsed.sessions || typeof parsed.sessions !== 'object') {
      console.warn(
        '[ChatModule] parsed.sessions missing or invalid, resetting'
      );
      return { sessions: {} };
    }

    return parsed;
  } catch (e) {
    console.warn(
      '[ChatModule] Failed to load chat history from localStorage',
      e
    );
    return { sessions: {} };
  }
}

function persistState(state) {
  try {
    const serialized = JSON.stringify(state);
    localStorage.setItem(STORAGE_KEY, serialized);
  } catch (e) {
    console.warn(
      '[ChatModule] Failed to persist chat history to localStorage',
      e
    );
  }
}

export default {
  namespaced: true,

  state: () => {
    const initial = loadPersistedState();
    return {
      ...initial, // { sessions: { [sessionKey]: { messages: [], interactions: [] } } }
    };
  },

  getters: {
    session: (state) => (sessionKey) => {
      const session = state.sessions[sessionKey] || {
        messages: [],
        interactions: [],
      };
      return session;
    },

    messages: (state, getters) => (sessionKey) => {
      const session = getters.session(sessionKey);
      const { messages } = session;

      return messages;
    },

    interactions: (state, getters) => (sessionKey) => {
      const session = getters.session(sessionKey);
      const { interactions } = session;

      return interactions;
    },
  },

  mutations: {
    INIT_SESSION(state, sessionKey) {
      const now = new Date().toISOString();

      if (!state.sessions[sessionKey]) {
        const session = {
          messages: [],
          interactions: [],
          createdAt: now,
          updatedAt: now,
        };

        Vue.set(state.sessions, sessionKey, session);
        persistState(state);
      }
    },

    RESET_SESSION(state, sessionKey) {
      const now = new Date().toISOString();

      const session = {
        messages: [],
        interactions: [],
        createdAt: now,
        updatedAt: now,
      };

      Vue.set(state.sessions, sessionKey, session);
      persistState(state);
    },

    APPEND_MESSAGE(state, { sessionKey, message }) {
      const now = new Date().toISOString();

      const existing = state.sessions[sessionKey] || {
        messages: [],
        interactions: [],
        createdAt: now,
        updatedAt: now,
      };

      const updatedSession = {
        ...existing,
        messages: [...existing.messages, message].slice(
          -MAX_MESSAGES_PER_SESSION
        ),
        updatedAt: now,
      };

      Vue.set(state.sessions, sessionKey, updatedSession);
      persistState(state);
    },

    APPEND_INTERACTION(state, { sessionKey, interaction }) {
      const now = new Date().toISOString();

      const existing = state.sessions[sessionKey] || {
        messages: [],
        interactions: [],
        createdAt: now,
        updatedAt: now,
      };

      const updatedSession = {
        ...existing,
        interactions: [...existing.interactions, interaction],
        updatedAt: now,
      };

      Vue.set(state.sessions, sessionKey, updatedSession);
      persistState(state);
    },
    REPLACE_INTERACTIONS(state, { sessionKey, interactions }) {
      const now = new Date().toISOString();

      const existing = state.sessions[sessionKey] || {
        messages: [],
        interactions: [],
        createdAt: now,
        updatedAt: now,
      };

      const updatedSession = {
        ...existing,
        interactions,
        updatedAt: now,
      };

      Vue.set(state.sessions, sessionKey, updatedSession);
      persistState(state);
    },
  },

  actions: {
    initSession({ commit }, sessionKey) {
      commit('INIT_SESSION', sessionKey);
    },

    resetSession({ commit }, sessionKey) {
      commit('RESET_SESSION', sessionKey);
    },

    appendMessage({ commit }, payload) {
      commit('APPEND_MESSAGE', payload);
    },

    appendInteraction({ commit }, payload) {
      commit('APPEND_INTERACTION', payload);
    },

    replaceInteractions({ commit }, payload) {
      commit('REPLACE_INTERACTIONS', payload);
    },

    cleanOldSessions({ state }, maxDays = 30) {
      const now = Date.now();
      const cutoff = now - maxDays * 24 * 60 * 60 * 1000;

      Object.keys(state.sessions).forEach((key) => {
        const session = state.sessions[key];

        if (!session || !session.updatedAt) return;

        const updatedTime = new Date(session.updatedAt).getTime();

        if (Number.isNaN(updatedTime) || updatedTime < cutoff) {
          Vue.delete(state.sessions, key);
        }
      });

      persistState(state);
    },
  },
};
